/* eslint-disable camelcase */
import { useCallback } from 'react';
import { refreshAdminMfaSession, revokeAdminMfaSession } from '~/api/adminMfa';
import { setConfig } from '~/contexts/stores/configStore';
import { setAdminMfaLocked, setAdminMfaSession } from '~/redux/modules/adminMfa';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

export const ADMIN_MFA_CHANNEL = 'admin-mfa';

let channel: BroadcastChannel | null = null;

const getChannel = () => {
    if (typeof BroadcastChannel === 'undefined') return null;

    if (!channel) channel = new BroadcastChannel(ADMIN_MFA_CHANNEL);

    return channel;
};

/**
 * Subscribe to session changes from *other* tabs.
 *
 * Deliberately reuses the module-level channel the broadcaster posts on, rather
 * than opening a second one. A BroadcastChannel never delivers to the object
 * that sent the message, but it does deliver to every other object on the same
 * name — including one in the same tab. The watcher used to open its own, so a
 * tab woke itself on its own broadcast and re-ran the access check it had just
 * triggered. Sharing the object makes self-delivery impossible.
 *
 * The channel is not closed on unsubscribe: it belongs to the module and the
 * broadcaster still needs it. Only the listener is removed.
 *
 * @param {Function} onChange called when another tab changes the session
 * @returns {Function} unsubscribe
 */
export const subscribeToAdminSessionChanges = (onChange: () => void) => {
    const active = getChannel();

    if (!active) return () => undefined;

    const handler = () => onChange();

    active.addEventListener('message', handler);

    return () => active.removeEventListener('message', handler);
};

/**
 * Store the session token the server just handed back — and leave the one we
 * hold alone when it hands back none.
 *
 * A missing token on a *valid* session means "keep the one you have". The
 * server only mints when the caller presented no token, or one that has been
 * evicted; a tab whose token still works is answered without one. Reading that
 * absence as "you have none" and clearing would take Admin away from a tab that
 * was working perfectly well.
 *
 * An invalid session still clears, because then there is genuinely nothing to
 * hold.
 *
 * @param {Object} [mfa] the mfa block from an admin access response
 * @returns {void}
 */
export const applyAdminMfaToken = (mfa?: { token?: string; valid?: boolean } | null) => {
    if (mfa?.token) {
        setConfig({ ADMIN_MFA_TOKEN: mfa.token });

        return;
    }

    if (mfa?.valid) return;

    setConfig({ ADMIN_MFA_TOKEN: undefined });
};

/**
 * Tell other tabs the session changed.
 *
 * Module-scoped rather than per-hook so the extend button and the navigation
 * effect share one channel. The listener lives in useAdminSessionWatcher.
 *
 * @param {String} type what happened
 * @returns {void}
 */
export const broadcastAdminSessionChange = (type: 'extended' | 'revoked') => {
    try {
        getChannel()?.postMessage({ type });
    } catch (error) {
        // A closed or unavailable channel is not worth failing a teardown over.
    }
};

/**
 * The two things that change an admin session: extend it, or end it.
 *
 * Deliberately effect-free. Both the expiry warning's buttons and the activity
 * refresh in useAdminSessionActivity need these, and if they lived in a hook
 * that also registered the input listeners, mounting it twice would attach two
 * sets of them and slide the session twice on every keystroke.
 *
 * @returns {Object} onRefresh and onRevoke callbacks
 */
const useAdminSessionActions = () => {
    const dispatch = useAppDispatch();
    const { id } = useAppSelector(({ profile }) => profile);

    const onRefresh = useCallback(async () => {
        if (!id) return false;

        try {
            const response = await refreshAdminMfaSession(id);
            const mfa = response?.mfa;

            if (mfa?.valid) {
                dispatch(setAdminMfaSession(mfa));
                broadcastAdminSessionChange('extended');

                return true;
            }

            // An explicit `valid: false` is the server saying the session has
            // gone, which the member does need to be told.
            if (mfa && mfa.valid === false) {
                dispatch(setAdminMfaSession(mfa));

                return false;
            }

            // Anything else — a 200 with no session on it, a truncated body, a
            // proxy's error page — says nothing about the session, so nothing
            // is dispatched. This used to fall through to `{ valid: false }`,
            // which ejected a working member to the code prompt on a malformed
            // response. Since this now runs on ordinary activity rather than
            // only on a button press, that would fire mid-keystroke.
            return false;
        } catch (error) {
            // The watcher's zero-check asks the server anyway, so a failed
            // slide needs no handling here beyond not throwing.
            return false;
        }
    }, [dispatch, id]);

    const onRevoke = useCallback(async () => {
        try {
            if (id) await revokeAdminMfaSession(id);
        } catch (error) {
            // Best-effort: the record lapses on its own, and the server-side
            // cascade on the refresh token covers sign-out.
        } finally {
            setConfig({ ADMIN_MFA_TOKEN: undefined });
            dispatch(setAdminMfaLocked());
            broadcastAdminSessionChange('revoked');
        }
    }, [dispatch, id]);

    return { onRefresh, onRevoke };
};

export default useAdminSessionActions;
